// DomandaScelte.tsx — v045: «Le tue scelte» chieste DOPO la primissima partita (prima della partita c'è solo la
// spiegazione del volto, obbligatoria). La mascotte tiene la finestra: «Sì a tutto» (il pulsante grande), «Scelgo io»
// (le spunte) o «Non ora». Il proprietario: «chiarezza, semplicità, opportunità».
// Chi ha detto no a qualcosa vede il promemoria (solo le voci rifiutate) una volta per apertura dell'app, a fine
// partita: «Sì, attivale» o «No, grazie».
// Valgono dal round dopo (anche nella rivincita con la stessa persona): il video si registra solo con il sì dato prima.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { CartaMascotte } from "./Guida.tsx";
import { ICONE_SCELTE, LinkLiberatoria, SpunteScelte } from "./InterruttoriScelte.tsx";
import { salvaScelte, scelteAttuali, segnaPromemoriaVisto, vociPossibili, vociRifiutate, type Scelte, type VoceScelta } from "../servizi/scelte.ts";
import { aggiornaIo, useAccount } from "../servizi/account.ts";
import { impostaClassifica } from "../servizi/social.ts";
import { eBooleano, leggiImpostazione } from "../servizi/impostazioni.ts";
import { vibra } from "../servizi/nativo.ts";
import { IMP_APP } from "../costanti.ts";

export function DomandaScelte({ modo, videoPossibile, onFine }: { modo: "prima" | "promemoria"; videoPossibile: boolean; onFine: () => void }) {
  const { t } = useTranslation();
  const account = useAccount();
  const io = account.tipo === "registrato" ? account.io : null;
  const [voci] = useState<VoceScelta[]>(() => (modo === "prima" ? vociPossibili(videoPossibile) : vociRifiutate(videoPossibile)));
  // il Wall of Fame si propone solo la prima volta, a chi ha l'account e può comparirci
  const conClassifica = modo === "prima" && !!io?.puoClassifica && !io.inClassifica && leggiImpostazione(IMP_APP.classificaAttiva, true, eBooleano);
  const [scelgo, setScelgo] = useState(false);
  const [valori, setValori] = useState<Scelte>(() => (modo === "prima" ? { foto: false, video: false, noi: false } : scelteAttuali()));
  const [classifica, setClassifica] = useState(false);
  const [invio, setInvio] = useState(false);

  const chiudi = () => {
    if (modo === "promemoria") segnaPromemoriaVisto();
    onFine();
  };
  const salva = (sc: Scelte, wall: boolean) => {
    setInvio(true);
    const lavori: Promise<unknown>[] = [salvaScelte(sc).catch(() => undefined)];
    if (wall && conClassifica && io) lavori.push(impostaClassifica(true, io.paese).then(() => aggiornaIo()).catch(() => undefined));
    // la finestra non aspetta il server (le scelte valgono subito sul telefono)
    void Promise.race([Promise.all(lavori), new Promise((ok) => setTimeout(ok, 1200))]).finally(chiudi);
  };
  const siATutto = () => {
    vibra("successo");
    const sc = { ...scelteAttuali(), ...(modo === "prima" ? { foto: false, video: false, noi: false } : {}) };
    for (const k of voci) sc[k] = true;
    salva(sc, true);
  };
  const nonOra = () => {
    // «Non ora» la prima volta = per ora no a tutto (il promemoria lo richiederà più avanti)
    if (modo === "prima") salva({ foto: false, video: false, noi: false }, false);
    else chiudi();
  };

  const titolo = modo === "prima" ? t("scelte.domanda_titolo") : t("scelte.promemoria_titolo");
  return (
    <CartaMascotte etichetta={titolo} onEsc={nonOra}>
      <h2 className="ld-mascotte-titolo">{titolo}</h2>
      <p className="ld-mascotte-testo">{modo === "prima" ? t("scelte.domanda_testo") : t("scelte.promemoria_testo")}</p>
      {scelgo ? (
        <div className="ld-domanda-spunte">
          <SpunteScelte valori={valori} onCambia={setValori} solo={voci} />
          {conClassifica && (
            <label className="ld-volto-spunta ld-scelta">
              <input type="checkbox" checked={classifica} onChange={(e) => setClassifica(e.target.checked)} />
              <span className="ld-scelta-testo">
                <span>
                  <span aria-hidden="true">🏆 </span>
                  {t("social.compari_classifica")}
                </span>
                <span className="ld-scelta-nota">{t("scelte.classifica_nota")}</span>
              </span>
            </label>
          )}
          <button type="button" className="ld-cta ld-cta-centrata" disabled={invio} onClick={() => salva(valori, classifica)}>
            {t("scelte.salva")}
          </button>
        </div>
      ) : (
        <>
          <ul className="ld-domanda-voci">
            {voci.map((k, i) => (
              <li key={k} style={{ animationDelay: `${200 + i * 160}ms` }}>
                <span className="ld-benvenuto-icona" aria-hidden="true">
                  {ICONE_SCELTE[k]}
                </span>
                <span>
                  <strong>{t(`scelte.${k}_breve_titolo`)}</strong>
                  <span className="ld-domanda-nota">
                    {t(`scelte.${k}_breve`)}
                    {k === "noi" && (
                      <>
                        {" "}
                        <LinkLiberatoria />
                      </>
                    )}
                  </span>
                </span>
              </li>
            ))}
            {conClassifica && (
              <li style={{ animationDelay: `${200 + voci.length * 160}ms` }}>
                <span className="ld-benvenuto-icona" aria-hidden="true">
                  🏆
                </span>
                <span>
                  <strong>{t("social.compari_classifica")}</strong>
                  <span className="ld-domanda-nota">{t("scelte.classifica_breve")}</span>
                </span>
              </li>
            )}
          </ul>
          <button type="button" className="ld-cta ld-cta-centrata ld-domanda-si" disabled={invio} onClick={siATutto}>
            {modo === "prima" ? t("scelte.si_tutto") : t("scelte.si_attiva")}
          </button>
          {modo === "prima" && (
            <button type="button" className="pulsante ld-domanda-scelgo" disabled={invio} onClick={() => setScelgo(true)}>
              {t("scelte.scelgo_io")}
            </button>
          )}
        </>
      )}
      <button type="button" className="pulsante-link ld-mascotte-salta" disabled={invio} onClick={nonOra}>
        {modo === "prima" ? t("scelte.non_ora") : t("scelte.no_grazie")}
      </button>
      <p className="ld-domanda-dopo">{t("scelte.dalla_prossima")} {io ? t("scelte.cambia_profilo") : t("scelte.cambia_partita")}</p>
    </CartaMascotte>
  );
}
